import React, { useState, useEffect } from 'react';
import './DashboardProviders.css';
import { supabase } from '../../lib/supabase';
import { logProviderActivity } from '../../lib/activityLogger';
import NotificationModal from '../NotificationModal/NotificationModal';

const NoDataState = ({ type, icon, message, actionText, onAction }) => (
  <div className="no-data-state">
    <div className="no-data-icon">{icon}</div>
    <h3>No {type} Found</h3>
    <p>{message}</p>
    {onAction && (
      <button className="add-first-btn" onClick={onAction}>{actionText}</button>
    )}
  </div>
);

const DashboardProviders = ({ providers, dataLoading, onProviderAdded }) => {
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('row'); // 'card' or 'row'
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [providerFormData, setProviderFormData] = useState({
    name: '',
    logo: '',
    website: '',
    is_active: true
  });
  const [logoFile, setLogoFile] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Sort by order_index primarily
  useEffect(() => {
    let filtered = [...providers];

    // Sort by orderIndex first, then alphabetical
    filtered = filtered.sort((a, b) => {
      const orderA = a.order_index ?? 0;
      const orderB = b.order_index ?? 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setFilteredProviders(filtered);
  }, [providers]);

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    // Make sure row click doesn't fire when starting a drag
    e.stopPropagation();

    setDraggedItem(index);
    // Needed for Firefox
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary for drop event to fire
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverItem(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    // When dropping, we rely on the explicit dropIndex for correctness
    if (draggedItem === null || dropIndex === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // 1. Reorder the array locally for optimistic UI update
    const newItems = [...filteredProviders];
    const draggedProvider = newItems[draggedItem];

    // Remove dragged item
    newItems.splice(draggedItem, 1);
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedProvider);

    // 2. Recalculate order_index for ALL items based on their new visual order
    const updatedProviders = newItems.map((provider, index) => ({
      ...provider,
      order_index: index
    }));

    setFilteredProviders(updatedProviders);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // 3. Save new order_index back to Supabase
      const updatePromises = updatedProviders.map(provider =>
        supabase
          .from('providers')
          .update({ order_index: provider.order_index, updated_at: new Date().toISOString() })
          .eq('id', provider.id)
      );

      await Promise.all(updatePromises);

    } catch (error) {

      showNotification('error', 'Reorder Failed', 'Failed to save the new order. Refresh to reset.');
    }
  };

  // Test storage connection on component mount
  useEffect(() => {
    testStorageConnection();
  }, []);

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    // Compare form data, but ignore logo file changes (handled separately)
    const currentData = { ...providerFormData };
    const initialData = { ...initialFormData };
    // Remove logo from comparison as it's handled via logoFile
    delete currentData.logo;
    delete initialData.logo;
    return JSON.stringify(currentData) !== JSON.stringify(initialData) || logoFile !== null;
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setProviderFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {

      setLogoFile(file);
      // Create a preview URL for the logo
      const reader = new FileReader();
      reader.onload = (event) => {

        setProviderFormData(prev => ({ ...prev, logo: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const testStorageConnection = async () => {
    try {

      const { data, error } = await supabase.storage.from('assets').list('providers', { limit: 1 });
      if (error) {

      } else {

      }
    } catch (err) {

    }
  };

  const uploadLogoToStorage = async (file, providerId = null) => {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `providers/${fileName}`;


      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from('assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {

        throw error;
      }


      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('assets')
        .getPublicUrl(filePath);




      // Test if the URL is accessible
      try {
        const response = await fetch(publicUrl, { method: 'HEAD' });

        if (!response.ok) {

        }
      } catch (fetchError) {

      }

      return publicUrl;

    } catch (error) {

      throw error;
    }
  };

  const openProviderDetails = (provider) => {
    setSelectedProvider(provider);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const startEditSelectedProvider = () => {
    if (!selectedProvider) return;
    // Prime the form with existing values
    setProviderFormData({
      name: selectedProvider.name || '',
      logo: selectedProvider.logo || '',
      website: selectedProvider.website || '',
      is_active: selectedProvider.is_active !== false
    });
    setLogoFile(null); // Reset file upload
    setIsEditing(true);
  };

  // Clear all provider caches from sessionStorage when providers are updated
  const clearProviderCaches = () => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('mds_providers_cache_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));

    } catch (e) {

    }
  };

  const handleUpdateProvider = async (e) => {
    e.preventDefault();
    if (!selectedProvider) return;
    setIsSubmitting(true);

    try {

      let logoUrl = selectedProvider.logo; // Keep existing logo by default

      // Upload new logo to storage if file is selected
      if (logoFile) {
        try {
          logoUrl = await uploadLogoToStorage(logoFile, selectedProvider.id);

        } catch (uploadError) {

          showNotification('error', 'Logo Upload Failed', 'Failed to upload logo image. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      const wasActive = selectedProvider.is_active !== false;
      const willBeActive = providerFormData.is_active === true;

      const { error } = await supabase
        .from('providers')
        .update({
          name: providerFormData.name,
          logo: logoUrl,
          website: providerFormData.website,
          supported_grades: [], // Providers are now available to all users in a school
          is_active: providerFormData.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProvider.id);

      if (error) {

        showNotification('error', 'Error Updating Provider', error.message);
      } else {
        // When deactivating, the provider stays in accessible_providers but won't show to students
        // because the student query filters by is_active = true. This allows reactivation without
        // needing to re-add the provider to schools.
        if (providerFormData.is_active === false) {

        }

        // Clear provider caches if status changed (activated/deactivated) to ensure students see updated list
        if (wasActive !== willBeActive) {
          clearProviderCaches();
        }

        // Log the activity
        await logProviderActivity.updated(providerFormData.name);

        showNotification('success', 'Provider Updated Successfully', `${providerFormData.name} has been updated successfully`);
        setShowDetailsModal(false);
        setIsEditing(false);
        setLogoFile(null); // Reset file upload
        onProviderAdded && onProviderAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Updating Provider', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProvider = () => {
    if (!selectedProvider) return;
    setItemToDelete({ type: 'provider', data: selectedProvider });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const providerId = itemToDelete.data.id;

      // First, remove this provider from all schools' accessible_providers arrays
      try {
        // Fetch all schools that have this provider in their accessible_providers
        const { data: schoolsWithProvider, error: fetchError } = await supabase
          .from('schools')
          .select('id, accessible_providers');

        if (fetchError) {

        } else if (schoolsWithProvider && schoolsWithProvider.length > 0) {
          // Update each school to remove the provider from accessible_providers
          const updatePromises = schoolsWithProvider
            .filter(school =>
              Array.isArray(school.accessible_providers) &&
              school.accessible_providers.includes(providerId)
            )
            .map(school => {
              const updatedProviders = school.accessible_providers.filter(
                id => id !== providerId
              );

              return supabase
                .from('schools')
                .update({
                  accessible_providers: updatedProviders,
                  updated_at: new Date().toISOString()
                })
                .eq('id', school.id);
            });

          const results = await Promise.all(updatePromises);
          const errors = results.filter(result => result.error);

          if (errors.length > 0) {

          } else {

          }
        }
      } catch (removeError) {

        // Continue with deletion even if school update fails
      }

      // Now delete the provider from the database
      const { error } = await supabase
        .from('providers')
        .delete()
        .eq('id', providerId);

      if (error) {

        showNotification('error', 'Error Deleting Provider', error.message);
      } else {
        // Clear provider caches since provider was deleted
        clearProviderCaches();

        // Log the activity
        await logProviderActivity.deleted(itemToDelete.data.name);

        showNotification('success', 'Provider Deleted Successfully', `${itemToDelete.data.name} has been deleted successfully and removed from all schools`);
        setShowDetailsModal(false);
        setSelectedProvider(null);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        onProviderAdded && onProviderAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Deleting Provider', err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleSubmitProvider = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Generate a unique key based on the provider name
      const providerKey = providerFormData.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');


      let logoUrl = null;

      // Upload logo to storage if file is selected
      if (logoFile) {
        try {
          logoUrl = await uploadLogoToStorage(logoFile);

        } catch (uploadError) {

          showNotification('error', 'Logo Upload Failed', 'Failed to upload logo image. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      const { error } = await supabase
        .from('providers')
        .insert([{
          name: providerFormData.name,
          key: providerKey,
          logo: logoUrl,
          website: providerFormData.website,
          supported_grades: [], // Providers are now available to all users in a school
          is_active: true,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {

        showNotification('error', 'Error Adding Provider', error.message);
      } else {
        // Log the activity
        await logProviderActivity.created(providerFormData.name);

        showNotification('success', 'Provider Added Successfully', `${providerFormData.name} has been added successfully`);
        setProviderFormData({
          name: '', logo: '', website: ''
        });
        setLogoFile(null); // Reset file upload
        setInitialFormData(null);
        setShowAddProviderModal(false);
        onProviderAdded && onProviderAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Adding Provider', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowAddProviderModal(false);
    setProviderFormData({
      name: '', logo: '', website: '', is_active: true
    });
    setLogoFile(null); // Reset file upload
    setInitialFormData(null);
  };

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddProviderModal) {
      setInitialFormData(JSON.parse(JSON.stringify(providerFormData)));
    } else {
      setInitialFormData(null);
    }
  }, [showAddProviderModal, providerFormData]);

  // Drag handle icon for table rows
  const DragHandleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="19" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="19" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );

  // SVG Icons
  const ProviderIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" fill="currentColor" />
    </svg>
  );

  const CardViewIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4ZM4 6V18H20V6H4Z" fill="currentColor" />
      <path d="M4 8H8V12H4V8Z" fill="currentColor" />
      <path d="M10 8H14V12H10V8Z" fill="currentColor" />
      <path d="M16 8H20V12H16V8Z" fill="currentColor" />
      <path d="M4 14H8V18H4V14Z" fill="currentColor" />
      <path d="M10 14H14V18H10V14Z" fill="currentColor" />
      <path d="M16 14H20V18H16V14Z" fill="currentColor" />
    </svg>
  );

  const RowViewIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6H20V8H4V6ZM4 11H20V13H4V11ZM4 16H20V18H4V16Z" fill="currentColor" />
    </svg>
  );


  return (
    <div className="dashboard-providers-content">
      <div className="dashboard-providers-content-header">
        <div className="dashboard-providers-header-main">
          <h2>Learning Providers ({filteredProviders.length} of {providers.length})</h2>
          <div className="dashboard-providers-header-actions">
            <button
              className="dashboard-providers-view-toggle"
              onClick={() => setViewMode(viewMode === 'row' ? 'card' : 'row')}
            >
              {viewMode === 'row' ? <CardViewIcon /> : <RowViewIcon />}
              {viewMode === 'row' ? 'Card View' : 'Row View'}
            </button>
            <button className="dashboard-providers-add-button" onClick={() => setShowAddProviderModal(true)}>+ Add Provider</button>
          </div>
        </div>
      </div>

      {dataLoading ? (
        <div className="loading-state">
          <div className="loading-spinner">⏳</div>
          <p>Loading providers...</p>
        </div>
      ) : filteredProviders.length === 0 ? (
        <NoDataState
          type="Providers"
          icon="📚"
          message="No providers have been added yet. Add your first provider to get started."
          actionText="+ Add First Provider"
          onAction={() => setShowAddProviderModal(true)}
        />
      ) : viewMode === 'card' ? (
        <div className="dashboard-providers-grid">
          {filteredProviders.map((provider) => (
            <div key={provider.id} className="dashboard-providers-card" onClick={() => openProviderDetails(provider)}>
              <div className="dashboard-providers-logo">
                {provider.logo ? (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    onError={(e) => {

                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div style={{ display: provider.logo ? 'none' : 'flex' }}>
                  <ProviderIcon />
                </div>
              </div>
              <h3>{provider.name || 'Unnamed Provider'}</h3>
              <div className="dashboard-providers-stats">
                {provider.website && <span>Website: {provider.website}</span>}
              </div>
              <div className={`dashboard-providers-status ${provider.is_active ? 'dashboard-providers-active' : 'dashboard-providers-inactive'}`}>
                {provider.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="dashboard-providers-table">
          <div className="dashboard-providers-table-header">
            <div className="dashboard-providers-table-cell dashboard-providers-header-cell drag-handle-header"></div>
            <div className="dashboard-providers-table-cell dashboard-providers-header-cell">Provider Name</div>
            <div className="dashboard-providers-table-cell dashboard-providers-header-cell">Website</div>
            <div className="dashboard-providers-table-cell dashboard-providers-header-cell">Status</div>
          </div>

          {filteredProviders.map((provider, index) => (
            <div
              key={provider.id}
              className={`dashboard-providers-table-row ${draggedItem === index ? 'is-dragging' : ''} ${dragOverItem === index ? 'drag-over' : ''}`}
              onClick={() => openProviderDetails(provider)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div
                className="dashboard-providers-table-cell dashboard-providers-data-cell drag-handle-cell"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, index)}
                onClick={(e) => e.stopPropagation()}
              >
                <DragHandleIcon />
              </div>
              <div className="dashboard-providers-table-cell dashboard-providers-data-cell">
                <div className="dashboard-providers-name-info">
                  <div className="dashboard-providers-name-avatar">
                    {provider.logo ? (
                      <img
                        src={provider.logo}
                        alt={provider.name}
                        onError={(e) => {

                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div style={{ display: provider.logo ? 'none' : 'flex' }}>
                      <ProviderIcon />
                    </div>
                  </div>
                  <div className="dashboard-providers-name-text">
                    <div className="dashboard-providers-name-main">{provider.name || 'Unnamed Provider'}</div>
                  </div>
                </div>
              </div>
              <div className="dashboard-providers-table-cell dashboard-providers-data-cell dashboard-providers-website-cell">
                <div className="dashboard-providers-website-info">
                  <div className="dashboard-providers-website-text">
                    {provider.website ? (
                      <a href={provider.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        {provider.website}
                      </a>
                    ) : (
                      <span className="dashboard-providers-no-website">No website</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="dashboard-providers-table-cell dashboard-providers-data-cell dashboard-providers-status-cell">
                <div className="dashboard-providers-status-info">
                  <span className={`dashboard-providers-status-badge ${provider.is_active ? 'dashboard-providers-status-active' : 'dashboard-providers-status-inactive'}`}>
                    {provider.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Provider Details / Edit Modal */}
      {showDetailsModal && selectedProvider && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit Provider' : 'Provider Details'}</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            {!isEditing ? (
              <div className="dashboard-providers-details">
                <div className="dashboard-providers-details-section">
                  <div className="dashboard-providers-section-header">
                    <div className="dashboard-providers-section-icon">
                      <ProviderIcon />
                    </div>
                    <h4>Basic Information</h4>
                  </div>
                  <div className="dashboard-providers-details-grid">
                    <div className="dashboard-providers-detail-item">
                      <span className="dashboard-providers-detail-label">Name</span>
                      <span className="dashboard-providers-detail-value">{selectedProvider.name || '—'}</span>
                    </div>
                    <div className="dashboard-providers-detail-item">
                      <span className="dashboard-providers-detail-label">Logo</span>
                      <span className="dashboard-providers-detail-value">
                        {selectedProvider.logo ? (
                          <div className="provider-logo-display">
                            <img
                              src={selectedProvider.logo}
                              alt={selectedProvider.name}
                              onError={(e) => {

                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'block';
                                }
                              }}
                            />
                            <span style={{ display: 'none' }}>Logo failed to load</span>
                          </div>
                        ) : (
                          <span>No logo</span>
                        )}
                      </span>
                    </div>
                    <div className="dashboard-providers-detail-item">
                      <span className="dashboard-providers-detail-label">Status</span>
                      <span className="dashboard-providers-detail-value">
                        <span className={`dashboard-providers-status-badge ${selectedProvider.is_active ? 'dashboard-providers-status-active' : 'dashboard-providers-status-inactive'}`}>
                          {selectedProvider.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dashboard-providers-section-divider"></div>

                <div className="dashboard-providers-details-section">
                  <div className="dashboard-providers-section-header">
                    <div className="dashboard-providers-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
                      </svg>
                    </div>
                    <h4>Additional Information</h4>
                  </div>
                  <div className="dashboard-providers-details-grid">
                    <div className="dashboard-providers-detail-item">
                      <span className="dashboard-providers-detail-label">Website</span>
                      <span className="dashboard-providers-detail-value">
                        {selectedProvider.website ? (
                          <a href={selectedProvider.website} target="_blank" rel="noopener noreferrer">
                            {selectedProvider.website}
                          </a>
                        ) : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dashboard-providers-form-actions">
                  <button type="button" className="dashboard-providers-cancel-btn" onClick={handleDeleteProvider}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                    </svg>
                    Delete Provider
                  </button>
                  <button type="button" className="dashboard-providers-submit-btn" onClick={startEditSelectedProvider}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.71 7.04C21.1 6.65 21.1 6 20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L18.87 8.87M3 17.25V21H6.75L17.81 9.93L14.06 6.18L3 17.25Z" />
                    </svg>
                    Edit Provider
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateProvider} className="dashboard-providers-form">
                <div className="dashboard-providers-form-row">
                  <div className="dashboard-providers-form-group">
                    <label htmlFor="name">Provider Name *</label>
                    <input type="text" id="name" name="name" value={providerFormData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="dashboard-providers-form-group">
                    <label htmlFor="website">Website URL</label>
                    <input type="url" id="website" name="website" value={providerFormData.website} onChange={handleInputChange} placeholder="https://example.com" />
                  </div>
                </div>

                <div className="dashboard-providers-form-row">
                  <div className="dashboard-providers-form-group full-width">
                    <label htmlFor="logo">Logo</label>
                    <input
                      type="file"
                      id="logo"
                      name="logo"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="file-input"
                    />
                    {providerFormData.logo && (
                      <div className="logo-preview">
                        <img src={providerFormData.logo} alt="Logo preview" />
                        <span>Logo selected</span>
                      </div>
                    )}
                  </div>
                </div>


                <div className="dashboard-providers-form-row">
                  <div className="dashboard-providers-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={providerFormData.is_active === true}
                        onChange={(e) => setProviderFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="dashboard-providers-form-actions">
                  <button type="button" className="dashboard-providers-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button type="submit" className="dashboard-providers-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddProviderModal && (
        <div className="modal-overlay" onClick={() => handleOverlayClick(closeModal)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Provider</h2>
              <button className="modal-close" onClick={() => handleOverlayClick(closeModal)}>×</button>
            </div>

            <form onSubmit={handleSubmitProvider} className="dashboard-providers-form">
              <div className="dashboard-providers-form-row">
                <div className="dashboard-providers-form-group">
                  <label htmlFor="name">Provider Name *</label>
                  <input type="text" id="name" name="name" value={providerFormData.name} onChange={handleInputChange} placeholder="Enter provider name" required />
                </div>
                <div className="dashboard-providers-form-group">
                  <label htmlFor="website">Website URL</label>
                  <input type="url" id="website" name="website" value={providerFormData.website} onChange={handleInputChange} placeholder="https://example.com" />
                </div>
              </div>

              <div className="dashboard-providers-form-row">
                <div className="dashboard-providers-form-group full-width">
                  <label htmlFor="logo">Logo</label>
                  <input
                    type="file"
                    id="logo"
                    name="logo"
                    accept="image/*"
                    onChange={handleLogoFileChange}
                    className="file-input"
                  />
                  {providerFormData.logo && (
                    <div className="logo-preview">
                      <img src={providerFormData.logo} alt="Logo preview" />
                      <span>Logo selected</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-providers-form-actions">
                <button type="button" className="dashboard-providers-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                <button type="submit" className="dashboard-providers-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding Provider...' : 'Add Provider'}</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && itemToDelete && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Provider</h2>
            </div>

            <div className="confirmation-content">
              <p className="confirmation-message">
                Are you sure you want to delete <strong>"{itemToDelete.data.name}"</strong>?
              </p>
              <p className="confirmation-warning">
                This action cannot be undone and will permanently remove all associated data.
              </p>
            </div>

            <div className="confirmation-actions">
              <button className="cancel-delete-btn" onClick={cancelDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        autoClose={true}
        autoCloseDelay={4000}
      />
    </div>
  );
};

export default DashboardProviders;


